import { useState } from "react";
import { CreditCard, Copy, Check, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PaymentModal = ({ isOpen, onClose, service, selectedPackage, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const [paymentStep, setPaymentStep] = useState("details"); // "details" or "bank"
  const [copiedField, setCopiedField] = useState(null);

  const bankDetails = {
    accountName: "Deepa Prashanth",
    bsb: "062-169",
    accountNumber: "10418740",
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContinueToPayment = async () => {
    if (formData.name && formData.email) {
      // Save the payment intent
      try {
        await onSubmit({
          service_type: service?.id,
          package_name: selectedPackage?.name,
          amount: selectedPackage?.price,
          name: formData.name,
          email: formData.email,
        });
      } catch (e) {
        // Continue anyway to show bank details
      }
      setPaymentStep("bank");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setPaymentStep("details");
    setFormData({ name: "", email: "" });
    onClose();
  };

  if (!service || !selectedPackage) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {paymentStep === "details" ? "Complete Your Purchase" : "Bank Transfer Details"}
          </DialogTitle>
          <DialogDescription>
            {service.title} - {selectedPackage.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Order Summary */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">{selectedPackage.name}</p>
                <p className="text-sm text-muted-foreground">{selectedPackage.description}</p>
              </div>
              <p className="text-2xl font-bold text-primary">${selectedPackage.price}</p>
            </div>
          </div>

          {paymentStep === "details" ? (
            /* Step 1: Customer Details */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="payment-name">Full Name *</Label>
                <Input
                  id="payment-name"
                  name="name"
                  data-testid="payment-name-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment-email">Email Address *</Label>
                <Input
                  id="payment-email"
                  name="email"
                  type="email"
                  data-testid="payment-email-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <Button
                data-testid="continue-to-payment-btn"
                onClick={handleContinueToPayment}
                disabled={!formData.name || !formData.email}
                className="w-full"
              >
                Continue to Payment
                <CreditCard className="ml-2 h-4 w-4" />
              </Button>
            </div>
          ) : (
            /* Step 2: Bank Transfer Details */
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground mb-4">
                <p>Paying as: <strong>{formData.name}</strong></p>
                <p>Confirmation will be sent to: <strong>{formData.email}</strong></p>
              </div>

              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Bank Transfer Details</h3>
                </div>

                <div className="space-y-3">
                  {/* Account Name */}
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Account Name</p>
                      <p className="font-medium">{bankDetails.accountName}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="copy-account-name"
                      onClick={() => copyToClipboard(bankDetails.accountName, "accountName")}
                    >
                      {copiedField === "accountName" ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* BSB */}
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">BSB</p>
                      <p className="font-medium font-mono">{bankDetails.bsb}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="copy-bsb"
                      onClick={() => copyToClipboard("062169", "bsb")}
                    >
                      {copiedField === "bsb" ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Account Number */}
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Account Number</p>
                      <p className="font-medium font-mono">{bankDetails.accountNumber}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="copy-account-number"
                      onClick={() => copyToClipboard(bankDetails.accountNumber, "accountNumber")}
                    >
                      {copiedField === "accountNumber" ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Amount */}
                  <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg border border-accent/30">
                    <div>
                      <p className="text-xs text-muted-foreground">Amount to Transfer</p>
                      <p className="font-bold text-lg text-primary">${selectedPackage.price} AUD</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="copy-amount"
                      onClick={() => copyToClipboard(selectedPackage.price.toString(), "amount")}
                    >
                      {copiedField === "amount" ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground text-center">
                  Please include your <strong>name</strong> as the payment reference.
                  <br />
                  Once payment is received, you'll receive a confirmation email.
                </p>
              </div>

              <Button
                data-testid="done-btn"
                onClick={handleClose}
                className="w-full"
              >
                Done
              </Button>

              <Button
                data-testid="back-to-details-btn"
                variant="ghost"
                size="sm"
                onClick={() => setPaymentStep("details")}
                className="w-full"
              >
                Back to Details
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
